import openModal from './Modal';
import { type ModalConfigName } from './modalConfigs';

/** The `$q.defer()` shape, which is all this needs of Angular. */
export type Deferred = {
	resolve: (value?: unknown) => void;
	reject: (reason?: unknown) => void;
};

/**
 * Opens a React modal on behalf of an `$upModal` caller, bridging the two
 * result conventions (WEB-10153).
 *
 * `$upModal.open` returns a promise that **resolves with the saved entity and
 * rejects on dismiss** (`upsales/common/components/ui/Modal.js:541-543`).
 * `openModal` returns nothing and reports through `onClose(result)`, passing no
 * result when the user dismissed.
 *
 * Reproducing the rejection is the point. `upEventActivity.js:458-463` opens the
 * edit-activity modal and saves its event details in `.then()`; if a dismiss
 * resolved, cancelling the modal would write to the event. So an absent result
 * rejects, and the rejection carries it through unchanged the way `$upModal`
 * did.
 *
 * Lives here rather than inside `Modal.js` so this half — the half that can lose
 * data if it is wrong — is unit-testable without an Angular harness.
 */
const forwardToReactModal = (name: ModalConfigName, params: Record<string, unknown>, deferred: Deferred) => {
	openModal(name, {
		...params,
		onClose: (result?: unknown) => {
			if (result) {
				deferred.resolve(result);
				return;
			}
			deferred.reject(result);
		}
	} as never);
};

export default forwardToReactModal;
